<template>
  <div>
    <div
      v-if="isLoading && selectedWIDs.length > 0"
      class="text-center p-4 my-4 bg-white rounded-lg shadow-md"
    >
      <p class="text-gray-500">Loading weekly trends data...</p>
    </div>
    <!-- Keyword Weekly Rank Trends -->
    <div
      v-if="!isLoading && selectedWIDs.length > 0 && weeklyKeywordDataAvailable"
      class="bg-white rounded-lg shadow-md p-5 mb-4 w-full"
    >
      <div class="flex justify-between items-center mb-3">
        <h3 class="text-lg font-semibold text-gray-700">Keyword Rank Trends</h3>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full border-collapse heatmap-table">
          <thead>
            <tr class="border-b-2 border-gray-200">
              <th
                class="text-left py-2 px-2 font-semibold text-gray-700 sticky-col bg-white z-10"
              >
                Keyword
              </th>
              <th
                v-for="week in keywordHeatmapUniqueWeeks"
                :key="week"
                class="text-center py-2 px-2 font-semibold text-gray-700 min-w-[100px] cursor-pointer hover:bg-gray-100"
                :title="`Sort by ${formatDisplayDate(week)}`"
                @click="sortKeywordByWeek(week)"
              >
                {{
                  formatDisplayDate(week, { month: "short", day: "numeric" })
                }}
                <v-icon small>
                  {{
                    keywordDateSort.week === week
                      ? keywordDateSort.direction === "asc"
                        ? "mdi-arrow-up"
                        : "mdi-arrow-down"
                      : "mdi-arrow-up"
                  }}
                </v-icon>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="keyword in paginatedWeeklyHeatmapKeywords"
              :key="keyword"
              class="border-b hover:bg-gray-50 transition-colors group"
            >
              <td
                class="py-2 px-2 sticky-col bg-white group-hover:bg-gray-50 z-[1]"
              >
                <span class="font-medium">{{ keyword }}</span>
              </td>
              <td
                v-for="week_col in keywordHeatmapUniqueWeeks"
                :key="week_col"
                class="text-center py-2 px-2"
              >
                <div
                  class="flex flex-col items-center"
                  v-if="
                    keywordWeeklyRankData[keyword] &&
                    keywordWeeklyRankData[keyword][week_col] !== undefined &&
                    keywordWeeklyRankData[keyword][week_col].rank !== undefined
                  "
                >
                  <span
                    class="py-1 px-3 rounded-full font-medium text-xs"
                    :class="
                      getRankClass(
                        keywordWeeklyRankData[keyword][week_col].rank
                      )
                    "
                  >
                    {{
                      formatRank(keywordWeeklyRankData[keyword][week_col].rank)
                    }}
                  </span>
                  <span
                    v-if="
                      keywordWeeklyRankData[keyword][week_col].count !==
                      undefined
                    "
                    class="text-xs text-gray-500 mt-1 block"
                    title="Number of locations"
                  >
                    <span class="font-semibold">{{ keywordWeeklyRankData[keyword][week_col].count }} </span>locations
                  </span>
                </div>
                <span v-else class="text-gray-400">-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        class="mt-4 flex justify-end items-center gap-4"
        v-if="
          allWeeklyHeatmapKeywords.length > 0 &&
          paginatedWeeklyHeatmapKeywords.length > 0
        "
      >
        <div class="flex items-center gap-2">
          <span class="text-sm text-gray-700">Keywords per page:</span>
          <v-select
            v-model="keywordHeatmapItemsPerPage"
            :items="heatmapItemsPerPageOptions"
            dense
            outlined
            hide-details
            class="max-w-[80px]"
          ></v-select>
        </div>
        <div class="text-sm text-gray-700">
          {{ keywordHeatmapDisplayRangeText }}
        </div>
        <div class="flex items-center">
          <v-btn
            icon
            small
            @click="prevKeywordHeatmapPage"
            :disabled="keywordHeatmapCurrentPage === 1"
            title="Previous page"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            @click="nextKeywordHeatmapPage"
            :disabled="keywordHeatmapCurrentPage === keywordHeatmapTotalPages"
            title="Next page"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>
      <div
        v-if="
          allWeeklyHeatmapKeywords.length > 0 &&
          paginatedWeeklyHeatmapKeywords.length === 0
        "
        class="text-center text-gray-500 py-4"
      >
        No keyword data for the current page.
      </div>
      <div
        v-if="allWeeklyHeatmapKeywords.length === 0"
        class="text-center text-gray-500 py-4"
      >
        No keyword data available for table view.
      </div>
    </div>

    <!-- Location Weekly Rank Trends -->
    <div
      v-if="
        !isLoading && selectedWIDs.length > 0 && weeklyLocationDataAvailable
      "
      class="bg-white rounded-lg shadow-md p-5 mb-4 w-full"
    >
      <div class="flex justify-between items-center mb-3">
        <h3 class="text-lg font-semibold text-gray-700">
          Location Rank Trends
        </h3>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full border-collapse heatmap-table">
          <thead>
            <tr class="border-b-2 border-gray-200">
              <th
                class="text-left py-2 px-2 font-semibold text-gray-700 sticky-col bg-white z-10"
              >
                Location
              </th>
              <th
                v-for="week in heatmapUniqueWeeks"
                :key="week"
                class="text-center py-2 px-2 font-semibold text-gray-700 min-w-[100px] cursor-pointer hover:bg-gray-100"
                :title="`Sort by ${formatDisplayDate(week)}`"
                @click="sortLocationByWeek(week)"
              >
                {{
                  formatDisplayDate(week, { month: "short", day: "numeric" })
                }}
                <v-icon small>
                  {{
                    locationDateSort.week === week
                      ? locationDateSort.direction === "asc"
                        ? "mdi-arrow-up"
                        : "mdi-arrow-down"
                      : "mdi-arrow-up"
                  }}
                </v-icon>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="store in paginatedHeatmapStores"
              :key="store.wid"
              class="border-b hover:bg-gray-50 transition-colors group"
            >
              <td
                class="py-2 px-2 sticky-col bg-white group-hover:bg-gray-50 z-[1]"
              >
                <div
                  class="font-medium text-sm text-primary-600 hover:text-primary-800"
                  :title="getStorePrimaryName(store.wid)"
                >
                  {{ truncateText(getStorePrimaryName(store.wid), 48) }}
                </div>
                <div class="text-xs text-gray-500">
                  {{ getStoreSecondaryAddress(store.wid) }}
                </div>
              </td>
              <td
                v-for="week_col in heatmapUniqueWeeks"
                :key="week_col"
                class="text-center py-2 px-2"
              >
                <span
                  v-if="
                    locationWeeklyRankData[store.wid] &&
                    locationWeeklyRankData[store.wid][week_col] !== undefined
                  "
                  class="py-1 px-3 rounded-full font-medium text-xs"
                  :class="
                    getRankClass(locationWeeklyRankData[store.wid][week_col])
                  "
                >
                  {{ formatRank(locationWeeklyRankData[store.wid][week_col]) }}
                </span>
                <span v-else-if="!hasSuccessfulRun(store.wid, week_col)">- </span>
                <span
                  v-else
                  class="text-red-700 py-1 px-3 rounded-full font-medium text-xs"
                  :class="getRankClass(21)"
                  >20+</span
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        class="mt-4 flex justify-end items-center gap-4"
        v-if="
          selectedStoresForHeatmap.length > 0 &&
          paginatedHeatmapStores.length > 0
        "
      >
        <div class="flex items-center gap-2">
          <span class="text-sm text-gray-700">Locations per page:</span>
          <v-select
            v-model="heatmapItemsPerPage"
            :items="heatmapItemsPerPageOptions"
            dense
            outlined
            hide-details
            class="max-w-[80px]"
          ></v-select>
        </div>
        <div class="text-sm text-gray-700">
          {{ heatmapDisplayRangeText }}
        </div>
        <div class="flex items-center">
          <v-btn
            icon
            small
            @click="prevHeatmapPage"
            :disabled="heatmapCurrentPage === 1"
            title="Previous page"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            @click="nextHeatmapPage"
            :disabled="heatmapCurrentPage === heatmapTotalPages"
            title="Next page"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>
      <div
        v-if="
          selectedStoresForHeatmap.length > 0 &&
          paginatedHeatmapStores.length === 0
        "
        class="text-center text-gray-500 py-4"
      >
        No location data for the current page.
      </div>
      <div
        v-if="selectedStoresForHeatmap.length === 0"
        class="text-center text-gray-500 py-4"
      >
        No locations selected or data available for table view.
      </div>
    </div>

    <div
      v-if="
        !isLoading &&
        selectedWIDs.length > 0 &&
        !weeklyLocationDataAvailable &&
        !weeklyKeywordDataAvailable
      "
      class="bg-white rounded-lg shadow-md p-5 text-center"
    >
      <p class="text-gray-500">
        No weekly trends data available for the selected locations.
      </p>
    </div>
    <div
      v-if="!isLoading && selectedWIDs.length === 0"
      class="bg-white rounded-lg shadow-md p-5 text-center"
    >
      <p class="text-gray-500">
        Please select at least one location to see weekly trends.
      </p>
    </div>
  </div>
</template>

<script>
import { httpsCallable } from "firebase/functions";
import { functions } from "@/utils/firebase.utils"; // Ensure this path is correct
import { colUsers, auth, db } from "@/utils/firebase.utils";

export default {
  name: "WeeklyHeatmaps",
  props: {
    stores: {
      type: Array,
      default: () => [],
    },
    selectedWIDs: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: false,
      avgLocationRankLast12Runs: [],
      weeklyKeywordRanks: [],
      heatmapCurrentPage: 1,
      heatmapItemsPerPage: 50,
      heatmapItemsPerPageOptions: [5, 10, 25, 50],
      keywordHeatmapCurrentPage: 1,
      keywordHeatmapItemsPerPage: 10,
      locationDateSort: { week: null, direction: "asc" },
      keywordDateSort: { week: null, direction: "asc" },
      successfulRunsByDate: {},
    };
  },
  computed: {
    successfulRunLookup() {
      const map = {};
      if (!this.successfulRunsByDate || !Array.isArray(this.successfulRunsByDate)) {
        return map;
      }
      this.successfulRunsByDate.forEach((run) => {
        if (run.date && Array.isArray(run.wids)) {
          map[run.date] = new Set(run.wids);
        }
      });
      return map;
    },
    weeklyLocationDataAvailable() {
      return (
        this.avgLocationRankLast12Runs &&
        this.avgLocationRankLast12Runs.length > 0 &&
        this.heatmapUniqueWeeks.length > 0 &&
        this.paginatedHeatmapStores.length > 0
      );
    },
    weeklyKeywordDataAvailable() {
      return (
        this.weeklyKeywordRanks &&
        this.weeklyKeywordRanks.length > 0 &&
        this.allWeeklyHeatmapKeywords.length > 0
      );
    },
    selectedStoresForHeatmap() {
      if (!this.stores || !this.selectedWIDs) return [];
      const selectedWIDsSet = new Set(this.selectedWIDs);
      return this.stores
        .filter((store) => selectedWIDsSet.has(store.wid))
        .sort((a, b) => {
          const nameA = (
            a.gmbProfile?.title ||
            a.display_name ||
            a.name ||
            ""
          ).toLowerCase();
          const nameB = (
            b.gmbProfile?.title ||
            b.display_name ||
            b.name ||
            ""
          ).toLowerCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
    },
    paginatedHeatmapStores() {
      if (!this.selectedStoresForHeatmap.length) return [];
      let storesToSort = [...this.selectedStoresForHeatmap];

      if (this.locationDateSort.week) {
        const sortWeek = this.locationDateSort.week;
        storesToSort.sort((a, b) => {
          const rankA_raw = this.locationWeeklyRankData[a.wid]?.[sortWeek];
          const rankB_raw = this.locationWeeklyRankData[b.wid]?.[sortWeek];

          let valA, valB;

          if (this.locationDateSort.direction === "asc") {
            // For ASC sort:
            // Ranks 1-20 are their direct value.
            // Ranks > 20 are treated as 21.
            // Undefined/null ranks are treated as Infinity (to go to the end).
            if (rankA_raw === undefined || rankA_raw === null) {
              valA = Infinity;
            } else if (rankA_raw > 20) {
              valA = 21;
            } else {
              valA = rankA_raw;
            }

            if (rankB_raw === undefined || rankB_raw === null) {
              valB = Infinity;
            } else if (rankB_raw > 20) {
              valB = 21;
            } else {
              valB = rankB_raw;
            }
          } else {
            // direction === "desc"
            // For DESC sort:
            // Undefined/null ranks (fallback "20+") are treated as Infinity (highest value, to go to the top).
            // Ranks > 20 (actual "20+") are treated as 21.
            // Ranks 1-20 are their direct value.
            // This ensures order: Undefined "20+" -> Actual >20 "20+" -> Ranks 20 down to 1.
            if (rankA_raw === undefined || rankA_raw === null) {
              valA = Infinity;
            } else if (rankA_raw > 20) {
              valA = 21;
            } else {
              valA = rankA_raw;
            }

            if (rankB_raw === undefined || rankB_raw === null) {
              valB = Infinity;
            } else if (rankB_raw > 20) {
              valB = 21;
            } else {
              valB = rankB_raw;
            }
          }

          // ASC sort: valA - valB (smaller valA comes first)
          // DESC sort: valB - valA (larger valB comes first)
          return this.locationDateSort.direction === "asc"
            ? valA - valB
            : valB - valA;
        });
      }

      const start = (this.heatmapCurrentPage - 1) * this.heatmapItemsPerPage;
      const end = start + this.heatmapItemsPerPage;
      return storesToSort.slice(start, end);
    },
    heatmapTotalPages() {
      if (!this.selectedStoresForHeatmap.length) return 0;
      return Math.ceil(
        this.selectedStoresForHeatmap.length / this.heatmapItemsPerPage
      );
    },
    heatmapDisplayRangeText() {
      if (!this.selectedStoresForHeatmap.length) return "0 of 0";
      const start =
        (this.heatmapCurrentPage - 1) * this.heatmapItemsPerPage + 1;
      const end = Math.min(
        this.heatmapCurrentPage * this.heatmapItemsPerPage,
        this.selectedStoresForHeatmap.length
      );
      return `${start}-${end} of ${this.selectedStoresForHeatmap.length}`;
    },
    heatmapUniqueWeeks() {
      if (
        !this.avgLocationRankLast12Runs ||
        this.avgLocationRankLast12Runs.length === 0
      ) {
        return [];
      }
      const weekSet = new Set(
        this.avgLocationRankLast12Runs.map((item) => item.week_start)
      );
      return Array.from(weekSet).sort((a, b) => new Date(b) - new Date(a));
    },
    locationWeeklyRankData() {
      const heatmapData = {};
      if (
        !this.avgLocationRankLast12Runs ||
        this.avgLocationRankLast12Runs.length === 0 ||
        !this.selectedWIDs ||
        this.selectedWIDs.length === 0
      ) {
        return heatmapData;
      }
      const selectedWidsSet = new Set(this.selectedWIDs);
      this.avgLocationRankLast12Runs.forEach((item) => {
        if (selectedWidsSet.has(item.wid)) {
          if (!heatmapData[item.wid]) {
            heatmapData[item.wid] = {};
          }
          heatmapData[item.wid][item.week_start] = item.avg_location_rank;
        }
      });
      return heatmapData;
    },
    allWeeklyHeatmapKeywords() {
      if (!this.weeklyKeywordRanks || this.weeklyKeywordRanks.length === 0)
        return [];
      const keywordSet = new Set(
        this.weeklyKeywordRanks.map((item) => item.keyword)
      );
      return Array.from(keywordSet).sort();
    },
    keywordHeatmapUniqueWeeks() {
      if (!this.weeklyKeywordRanks || this.weeklyKeywordRanks.length === 0)
        return [];
      const weekSet = new Set(
        this.weeklyKeywordRanks.map((item) => item.week_start)
      );
      return Array.from(weekSet).sort((a, b) => new Date(b) - new Date(a));
    },
    keywordWeeklyRankData() {
      const heatmapData = {};
      if (!this.weeklyKeywordRanks || this.weeklyKeywordRanks.length === 0)
        return heatmapData;
      this.weeklyKeywordRanks.forEach((item) => {
        if (!heatmapData[item.keyword]) {
          heatmapData[item.keyword] = {};
        }
        heatmapData[item.keyword][item.week_start] = {
          rank: item.avg_keyword_rank,
          count: item.locations_count,
        };
      });
      return heatmapData;
    },
    paginatedWeeklyHeatmapKeywords() {
      if (!this.allWeeklyHeatmapKeywords.length) return [];
      let keywordsToSort = [...this.allWeeklyHeatmapKeywords];

      if (this.keywordDateSort.week) {
        const sortWeek = this.keywordDateSort.week;
        keywordsToSort.sort((a, b) => {
          const dataA = this.keywordWeeklyRankData[a]?.[sortWeek];
          const dataB = this.keywordWeeklyRankData[b]?.[sortWeek];

          const rankA = dataA?.rank;
          const rankB = dataB?.rank;

          const valA =
            rankA === undefined || rankA === null
              ? this.keywordDateSort.direction === "asc"
                ? Infinity
                : -Infinity
              : rankA > 20
              ? 21
              : rankA;
          const valB =
            rankB === undefined || rankB === null
              ? this.keywordDateSort.direction === "asc"
                ? Infinity
                : -Infinity
              : rankB > 20
              ? 21
              : rankB;

          return this.keywordDateSort.direction === "asc"
            ? valA - valB
            : valB - valA;
        });
      }

      const start =
        (this.keywordHeatmapCurrentPage - 1) * this.keywordHeatmapItemsPerPage;
      const end = start + this.keywordHeatmapItemsPerPage;
      return keywordsToSort.slice(start, end);
    },
    keywordHeatmapTotalPages() {
      if (!this.allWeeklyHeatmapKeywords.length) return 0;
      return Math.ceil(
        this.allWeeklyHeatmapKeywords.length / this.keywordHeatmapItemsPerPage
      );
    },
    keywordHeatmapDisplayRangeText() {
      if (!this.allWeeklyHeatmapKeywords.length) return "0 of 0";
      const start =
        (this.keywordHeatmapCurrentPage - 1) * this.keywordHeatmapItemsPerPage +
        1;
      const end = Math.min(
        this.keywordHeatmapCurrentPage * this.keywordHeatmapItemsPerPage,
        this.allWeeklyHeatmapKeywords.length
      );
      return `${start}-${end} of ${this.allWeeklyHeatmapKeywords.length}`;
    },
  },
  watch: {
    currentUID(newUID, oldUID) {
      if (newUID && newUID !== oldUID) {
        this.loadKeywordRanks();
      }
    },
    selectedWIDs: {
      handler() {
        this.heatmapCurrentPage = 1;
        this.keywordHeatmapCurrentPage = 1;
      },
      deep: true,
    },
    heatmapItemsPerPage() {
      this.heatmapCurrentPage = 1;
    },
    keywordHeatmapItemsPerPage() {
      this.keywordHeatmapCurrentPage = 1;
    },
  },
  mounted() {
    if (this.currentUID) {
      this.loadKeywordRanks();
    }
  },
  beforeDestroy() {},
  methods: {
    hasSuccessfulRun(wid, date) {
      return this.successfulRunLookup[date]?.has(wid) || false;
    },
    async loadKeywordRanks() {
      if (!this.currentUID) {
        console.warn(
          "WeeklyHeatmaps: currentUID is not available. Cannot load keyword ranks."
        );
        this.avgLocationRankLast12Runs = [];
        this.weeklyKeywordRanks = [];
        return;
      }
      this.isLoading = true;
      try {
        const idToken = await auth.currentUser.getIdToken();
        // check how much time it tokk to get the response
        const startTime = Date.now();
        const response = await fetch(
          `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/fetchWeeklyRanksReport`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({uid: this.currentUID, wids: this.selectedWIDs }),
          }
        );
        if (response.ok) {
          const endTime = Date.now();
          const duration = endTime - startTime;
          console.log(`Time taken: ${duration} milliseconds`);
          const result = await response.json(); // <-- THIS IS THE KEY STEP
          // console.log("Success! Data from server:", result);
          if (result.data && result.data) {
          this.avgLocationRankLast12Runs =
            result.data.avgLocationRankLast12Runs || [];
            this.weeklyKeywordRanks = result.data.weeklyKeywordRanks || [];
            this.successfulRunsByDate = result.data.successfulRunsByDate || [];
          } else {
            this.avgLocationRankLast12Runs = [];
            this.weeklyKeywordRanks = [];
          }
        }
      } catch (error) {
        console.error("Error loading weekly heatmap ranks:", error);
        this.avgLocationRankLast12Runs = [];
        this.weeklyKeywordRanks = [];
      } finally {
        this.isLoading = false;
      }
    },
    getRankClass(rank) {
      if (rank <= 3) return "bg-green-100 text-green-800";
      if (rank <= 5) return "bg-green-50 text-green-700";
      if (rank <= 10) return "bg-blue-50 text-blue-700";
      if (rank <= 20) return "bg-yellow-50 text-yellow-700";
      return "bg-red-50 text-red-700";
    },
    formatRank(rank) {
      if (rank > 20) return "20+";
      if (rank === null || rank === undefined) return "-";
      return Math.round(rank);
    },
    formatDisplayDate(
      dateString,
      options = { month: "short", day: "numeric", year: "numeric" }
    ) {
      if (!dateString) return "Latest Data";
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString("en-US", options);
        }
      } catch (e) {
        // Not a valid date format
      }
      return dateString;
    },
    getStorePrimaryName(wid) {
      const store = this.stores.find((s) => s.wid === wid);
      if (store) {
        return (
          store.gmbProfile?.title ||
          store.display_name ||
          store.name ||
          `Location (${wid})`
        );
      }
      return `Location (${wid})`;
    },
    getStoreSecondaryAddress(wid) {
      const store = this.stores.find((s) => s.wid === wid);
      if (store) {
        return store.sub_locality || store.city || "";
      }
      return "";
    },
    nextHeatmapPage() {
      if (this.heatmapCurrentPage < this.heatmapTotalPages) {
        this.heatmapCurrentPage++;
      }
    },
    prevHeatmapPage() {
      if (this.heatmapCurrentPage > 1) {
        this.heatmapCurrentPage--;
      }
    },
    nextKeywordHeatmapPage() {
      if (this.keywordHeatmapCurrentPage < this.keywordHeatmapTotalPages) {
        this.keywordHeatmapCurrentPage++;
      }
    },
    prevKeywordHeatmapPage() {
      if (this.keywordHeatmapCurrentPage > 1) {
        this.keywordHeatmapCurrentPage--;
      }
    },
    sortLocationByWeek(week) {
      if (this.locationDateSort.week === week) {
        this.locationDateSort.direction =
          this.locationDateSort.direction === "asc" ? "desc" : "asc";
      } else {
        this.locationDateSort.week = week;
        this.locationDateSort.direction = "asc";
      }
      this.heatmapCurrentPage = 1; // Reset to first page after sorting
    },
    sortKeywordByWeek(week) {
      if (this.keywordDateSort.week === week) {
        this.keywordDateSort.direction =
          this.keywordDateSort.direction === "asc" ? "desc" : "asc";
      } else {
        this.keywordDateSort.week = week;
        this.keywordDateSort.direction = "asc";
      }
      this.keywordHeatmapCurrentPage = 1; // Reset to first page
    },
    truncateText(text, maxLength) {
      if (text && text.length > maxLength) {
        return text.substring(0, maxLength) + "...";
      }
      return text;
    },
  },
};
</script>

<style scoped>
.heatmap-table {
  /* Styles for heatmap table, can be copied or adapted from GMBCompetitorDashboard */
}
.heatmap-table thead th.sticky-col,
.heatmap-table tbody td.sticky-col {
  position: -webkit-sticky; /* Safari */
  position: sticky;
  left: 0;
  background-color: white; /* Or parent background */
  z-index: 2; /* Ensure it's above other cells */
}
.heatmap-table tbody tr:hover td.sticky-col {
  background-color: #f9fafb; /* Tailwind gray-50 */
}

.heatmap-table thead th.sticky-col {
  z-index: 3; /* Header sticky column above body sticky column */
}

.heatmap-table th div.truncate,
.heatmap-table td div.truncate {
  max-width: 150px; /* Adjust as needed */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0 auto; /* For centering if text is shorter */
}
/* Add other relevant styles from GMBCompetitorDashboard.vue if needed */
</style>
